import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ExternalLink,
  Calendar,
  Tag,
  Target,
  Lightbulb,
  CheckCircle2,
  MessageSquare,
  Sparkles,
  Building2,
} from 'lucide-react';
import { format } from 'date-fns';
import { NewsIntelligenceRow } from '@/hooks/useNewsIntelligence';

interface ArticleDetailModalProps {
  article: NewsIntelligenceRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const getPriorityColor = (level: number | null) => {
  switch (level) {
    case 1: return 'bg-red-500 text-white';
    case 2: return 'bg-yellow-500 text-white';
    default: return 'bg-gray-200 text-gray-800';
  }
};

const getPriorityLabel = (level: number | null) => {
  switch (level) {
    case 1: return 'High Priority';
    case 2: return 'Medium Priority';
    default: return 'Low Priority';
  }
};

export function ArticleDetailModal({ article, open, onOpenChange }: ArticleDetailModalProps) {
  if (!article) return null;

  const actionItems = Array.isArray(article.action_items) ? article.action_items : [];
  const relatedArticles = Array.isArray(article.related_articles) ? article.related_articles : [];
  const enrichmentMeta = article.enrichment_metadata as {
    why_it_matters?: string;
    recommended_action?: string;
    opportunity_type?: string;
  } | null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-xl pr-8">
            {article.title || 'Article Details'}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh] pr-4">
          <div className="space-y-6">
            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-3">
              {article.published_at && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  {format(new Date(article.published_at), 'MMM d, yyyy')}
                </div>
              )}
              {article.source_feed && (
                <Badge variant="secondary">{article.source_feed}</Badge>
              )}
              <Badge className={getPriorityColor(article.priority_level)}>
                {getPriorityLabel(article.priority_level)}
              </Badge>
              {article.signal_type && (
                <Badge variant="outline" className="capitalize">
                  {article.signal_type.replace(/_/g, ' ')}
                </Badge>
              )}
              {article.url && (
                <a
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <ExternalLink className="h-4 w-4" />
                  Read Article
                </a>
              )}
            </div>

            {/* Alert Badges */}
            <div className="flex flex-wrap gap-2">
              {article.is_prospect_opportunity && (
                <Badge className="bg-green-500 text-white">
                  <Target className="h-3 w-3 mr-1" />
                  Prospect Opportunity
                </Badge>
              )}
              {article.is_competitor_alert && (
                <Badge className="bg-orange-500 text-white">
                  <Building2 className="h-3 w-3 mr-1" />
                  Competitor Alert
                </Badge>
              )}
              {article.is_investor_activity && (
                <Badge className="bg-purple-500 text-white">
                  <Sparkles className="h-3 w-3 mr-1" />
                  Investor Activity
                </Badge>
              )}
            </div>

            {/* Matched Keywords */}
            {article.matched_keywords && article.matched_keywords.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Matched Keywords
                </h4>
                <div className="flex flex-wrap gap-1">
                  {article.matched_keywords.map((kw, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {kw}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Prospect Info */}
            {article.prospect_company_name && (
              <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Target className="h-4 w-4 text-green-600" />
                  Identified Prospect
                </h4>
                <p className="font-semibold">{article.prospect_company_name}</p>
                {article.prospect_confidence && (
                  <p className="text-sm text-muted-foreground">
                    Confidence: {article.prospect_confidence}
                  </p>
                )}
              </div>
            )}

            <Separator />

            {/* Executive Briefing */}
            {article.executive_briefing && (
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-yellow-500" />
                  Executive Briefing
                </h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {article.executive_briefing}
                </p>
              </div>
            )}

            {/* Opportunity Analysis */}
            {article.opportunity_analysis && (
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Target className="h-4 w-4 text-blue-500" />
                  Opportunity Analysis
                </h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {article.opportunity_analysis}
                </p>
              </div>
            )}

            {/* Action Items */}
            {actionItems.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Action Items
                </h4>
                <ul className="space-y-2">
                  {actionItems.map((item: any, idx: number) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2 text-sm text-muted-foreground"
                    >
                      <span className="text-primary mt-0.5">•</span>
                      <span>
                        {typeof item === 'string' ? item : item?.action || item?.text || JSON.stringify(item)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Outreach Guidance */}
            {article.outreach_guidance && (
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-purple-500" />
                  Outreach Guidance
                </h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {article.outreach_guidance}
                </p>
              </div>
            )}

            {/* AI Client Insight */}
            {article.enriched_content && (
              <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950 dark:to-purple-950 rounded-lg border border-indigo-200 dark:border-indigo-800">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-indigo-500" />
                  AI Client Insight
                  {enrichmentMeta?.opportunity_type && (
                    <span className="ml-2 text-xs px-2 py-0.5 border rounded capitalize">
                      {enrichmentMeta.opportunity_type.replace(/_/g, ' ')}
                    </span>
                  )}
                </h4>
                <p className="text-sm whitespace-pre-wrap">
                  {article.enriched_content}
                </p>
              </div>
            )}

            {/* Why It Matters */}
            {enrichmentMeta?.why_it_matters && (
              <div className="p-4 bg-amber-50 dark:bg-amber-950 rounded-lg">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-amber-500" />
                  Why It Matters
                </h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {enrichmentMeta.why_it_matters}
                </p>
              </div>
            )}

            {/* Recommended Action */}
            {enrichmentMeta?.recommended_action && (
              <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Target className="h-4 w-4 text-green-500" />
                  Recommended Action
                </h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {enrichmentMeta.recommended_action}
                </p>
              </div>
            )}

            {/* Market Context */}
            {article.market_context && (
              <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-blue-500" />
                  Market Context
                </h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {article.market_context}
                </p>
              </div>
            )}

            {/* Key Companies */}
            {article.companies_mentioned && article.companies_mentioned.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-slate-500" />
                  Key Companies
                </h4>
                <div className="flex flex-wrap gap-1">
                  {article.companies_mentioned.map((company: string, idx: number) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {company}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <ExternalLink className="h-4 w-4 text-blue-500" />
                  Related Articles
                </h4>
                <ul className="space-y-2">
                  {relatedArticles.slice(0, 5).map((ra: any, idx: number) => (
                    <li key={idx} className="text-sm">
                      <a
                        href={ra.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {ra.title}
                      </a>
                      {ra.source && (
                        <span className="text-muted-foreground ml-2">
                          — {ra.source}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Match Reasoning */}
            {article.match_reasoning && (
              <div className="text-xs text-muted-foreground p-3 bg-muted rounded">
                <strong>Match Reasoning:</strong> {article.match_reasoning}
              </div>
            )}

            {/* Generate LinkedIn Post Button */}
            {article.linkedin_post_draft && (
              <div className="pt-4">
                <Button
                  className="w-full"
                  onClick={() => {
                    navigator.clipboard.writeText(article.linkedin_post_draft || '');
                  }}
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Copy LinkedIn Post Draft
                </Button>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
